import { ZONE_LABEL_MAP } from "../../../constants/zones";
import { applyFilterState } from "../../../lib/reportNew/filter-stateNew";
import { inferObjectFields } from "../../../lib/reportNew/fieldsNew";
import { filtrarProyectosExcluidos } from "../../../components/common/Inputs/ExcluirRaeeZeroWasteToggle/ExcluirRaeeZeroWasteToggle";

/*
    Pantallas de indicador: de dónde salen sus filas, con qué campos se filtran y
    cómo les afecta el checkbox de RAAES / Zero Waste.

    La usan las propias pantallas y el Informe Completo. Así las filas de cada hoja
    del Excel de indicadores son, por construcción, las mismas que vería su pantalla
    con el mismo filtro: no hay una segunda copia de la lógica que pueda separarse.
*/

const FILTER_FIELDS_INDICADOR = [
    {
        key: "rangoFechas",
        label: "Rango de fechas",
        type: "dateRange",
        rangeEndField: "fechaTratamiento",
    },
    { key: "pais", label: "País", type: "enum" },
    { key: "anio", label: "Año", type: "enum" },
    {
        key: "codUbicacion",
        label: "Ubicación",
        type: "enum",
        labelMap: ["", "Calle", "Centro comercial"],
    },
    { key: "intermediario", label: "Intermediario", type: "enum" },
    { key: "provincia", label: "Provincia", type: "enum" },
    { key: "tipoObra", label: "Tipo de obra", type: "enum" },
    {
        key: "codZona",
        label: "Zona",
        type: "enum",
        labelMap: ZONE_LABEL_MAP,
    },
];

export const QUICK_FILTERS_INDICADOR = [
    "rangoFechas",
    "codUbicacion",
    "intermediario",
    "provincia",
    "pais",
    "anio",
    "tipoObra",
    "codZona",
];

/* Cómo afecta el checkbox de RAAES / Zero Waste a las filas:
   - FILAS: quita las filas de esos proyectos.
   - PARAMETRO: no quita filas; el backend descuenta esas toneladas dentro de
     cada obra, así que viaja como parámetro del export.
   - NINGUNO: la pantalla no tiene el checkbox. */
export const EXCLUIR_RAEE = {
    FILAS: "filas",
    PARAMETRO: "parametro",
    NINGUNO: "ninguno",
};

const filasDeArray = (data) => (Array.isArray(data) ? data : []);

export const INDICADORES = {
    segregacionTotal: {
        datosUrl: () => `/api/Informe/GetSegregacionTotal`,
        filasDe: filasDeArray,
        filterFields: FILTER_FIELDS_INDICADOR,
        excludeFields: ["codTipoObra", "codCadena", "codIntermediario", "ubicacion", "codProvincia", "codPais", "codigo", "codFila", "codProyecto"],
        excluirRaee: EXCLUIR_RAEE.FILAS,
    },
    datosValorizacion: {
        datosUrl: () => `/api/Informe/GetDatosValorizacion`,
        filasDe: filasDeArray,
        filterFields: FILTER_FIELDS_INDICADOR,
        excludeFields: ["codTipoObra", "codCadena", "codIntermediario", "ubicacion", "codProvincia", "codPais", "codProyecto", "codFila"],
        /* Cada fila es una obra y el proyecto es del residuo: quitando filas se
           sacaban obras enteras del recuento. */
        excluirRaee: EXCLUIR_RAEE.PARAMETRO,
    },
    toneladasValorizadas: {
        datosUrl: () => `/api/Informe/GetToneladasValorizadas`,
        filasDe: filasDeArray,
        filterFields: FILTER_FIELDS_INDICADOR,
        excludeFields: ["codTipoObra", "codIntermediario", "ubicacion", "codProvincia", "codPais", "codFila", "codProyecto"],
        excluirRaee: EXCLUIR_RAEE.FILAS,
    },
    costesGestion: {
        datosUrl: (moneda) => `/api/Informe/GetCostesGestion?moneda=${moneda}`,
        filasDe: filasDeArray,
        filterFields: FILTER_FIELDS_INDICADOR,
        excludeFields: ["codTipoObra", "codCadena", "codIntermediario", "ubicacion", "codProvincia", "codPais", "codFila", "conCostes", "codProyecto", "moneda", "simbolo"],
        excluirRaee: EXCLUIR_RAEE.FILAS,
    },
    /* Sirve para las dos hojas de Abono Chatarra (importe y % de obras): la
       pantalla usa las mismas filas para las dos vistas. */
    abonoChatarra: {
        datosUrl: (moneda) => `/api/Informe/GetAbonoChatarra?moneda=${moneda}`,
        filasDe: filasDeArray,
        filterFields: FILTER_FIELDS_INDICADOR,
        excludeFields: ["codTipoObra", "codIntermediario", "ubicacion", "codProvincia", "codPais", "codigo", "codEspacio", "codIntegracion", "codFila", "codProyecto", "simbolo", "moneda", "residuoNombre"],
        excluirRaee: EXCLUIR_RAEE.NINGUNO,
    },
    ahorroSegregacion: {
        datosUrl: (moneda) => `/api/Informe/GetAhorroSegregacion?moneda=${moneda}`,
        filasDe: (data) => (Array.isArray(data?.residuos) ? data.residuos : []),
        filterFields: FILTER_FIELDS_INDICADOR,
        excludeFields: ["fechaTratamiento", "codigo", "anioResiduo", "anio", "codPais", "codTipoObra", "codIntermediario", "codUbicacion", "codProvincia", "codFila", "codResiduo", "codProyecto", "moneda", "factorMoneda", "simbolo", "ubicacion"],
        excluirRaee: EXCLUIR_RAEE.FILAS,
    },
};

export const mapaPaises = (paises) =>
    Object.fromEntries(paises.map((p) => [String(p.codPais), p.pais]));

export const mapaTiposObra = (tiposObra) =>
    Object.fromEntries(
        tiposObra.map((t) => [String(t.codTipoObra), t.descripcion])
    );

/* País y tipo de obra llegan como código; los filtros trabajan con el nombre. */
export function normalizarFilas(filas, mapPaises, mapTiposObra) {
    return filas.map((item) => ({
        ...item,
        pais: mapPaises[String(item.codPais)] ?? String(item.codPais ?? ""),
        tipoObra:
            mapTiposObra[String(item.codTipoObra)] ??
            String(item.codTipoObra ?? ""),
    }));
}

/* Las filas que muestra la pantalla del indicador con ese filtro. */
export function filasVisibles(indicador, filas, filterState, excluirRaeeZeroWaste) {
    const filtradas = applyFilterState(filas, filterState, indicador.filterFields);
    return indicador.excluirRaee === EXCLUIR_RAEE.FILAS
        ? filtrarProyectosExcluidos(filtradas, excluirRaeeZeroWaste)
        : filtradas;
}

/* Campos que se pueden filtrar en la pantalla del indicador: sus filtros rápidos
   y los que ofrece el constructor avanzado, que son los declarados más los de las
   filas, menos los excluidos. Es el mismo cálculo que hace FilterBar. */
function camposFiltrables(indicador, filas) {
    const excluidos = new Set(indicador.excludeFields);
    const campos = new Set(QUICK_FILTERS_INDICADOR);
    [...indicador.filterFields, ...inferObjectFields(filas)].forEach((field) => {
        if (!excluidos.has(field.key)) campos.add(field.key);
    });
    return campos;
}

/*
    El filtro de una pantalla, tal y como quedaría puesto en la pantalla de este
    indicador. Se trasladan la búsqueda libre, los filtros rápidos que esta pantalla
    también tiene y las condiciones avanzadas sobre campos que se pueden filtrar en
    ella; cada uno se interpreta con la definición de esta pantalla.

    Lo que aquí no existe (p. ej. Cadena o Centro de producción desde Costes) se
    ignora: estas filas no tienen ese campo y aplicarlo dejaría la hoja vacía.

    `filas` son las filas normalizadas del indicador, de las que FilterBar saca
    los campos avanzados. Devuelve también los campos ignorados.
*/
export function trasladarFiltro(filterState, indicador, filas) {
    const filtrables = camposFiltrables(indicador, filas);
    const ignorados = [];

    const quick = Object.fromEntries(
        QUICK_FILTERS_INDICADOR.map((campo) => [campo, []])
    );
    Object.entries(filterState?.quick || {}).forEach(([campo, valores]) => {
        if (!Array.isArray(valores) || valores.length === 0) return;
        if (QUICK_FILTERS_INDICADOR.includes(campo)) quick[campo] = valores;
        else ignorados.push(campo);
    });

    const conditions = (filterState?.advanced?.conditions || []).filter(
        (condition) => {
            if (!condition?.field) return false;
            if (filtrables.has(condition.field)) return true;
            ignorados.push(condition.field);
            return false;
        }
    );

    return {
        filterState: {
            search: filterState?.search || "",
            quick,
            advanced: {
                logic: filterState?.advanced?.logic || "AND",
                conditions,
            },
        },
        ignorados,
    };
}
